import { App } from "vue";
import axios, { AxiosRequestConfig } from "axios";
// This has to be brought in before `signalr` so that window.jQuery can be patched before `signalr` is loaded
import "./nhs/helper";
import "signalr";

// ENG-21695 jQuery/SignalR globals are injected at runtime by ./nhs/helper + signalr; no bundled types
declare const $: any;

interface SigningServiceApi {
  port: () => Promise<unknown>;
  sign: (jwt: string) => Promise<unknown>;
  version: () => Promise<unknown>;
}

declare global {
  interface Window {
    signingService: SigningServiceApi;
  }
}

export function nhsSigningService() {
  return {
    install(app: App) {
      const globalProperties = app.config.globalProperties;
      window.signingService = globalProperties.$signingService = SigningService();
    },
  };
}

const SigningService = (): SigningServiceApi => {
  const ports = [43487, 44213, 45031, 46478, 48853];
  // These two endpoints are HTTP by design - the NHS Credential Manager uses HTTP locally and not HTTPS.
  // No PII is sent as part of these requests
  const rootPortNumberUrl = "http://localhost:{0}/PRS/GetPortNumber";
  const rootSignalUrl = "http://localhost:{0}/signalr";

  const getConnection = async () => {
    const signingPort = await port();
    if (!signingPort) {
      throw new Error("Unable to determine NHS Signing Service port");
    }

    const connection = $.hubConnection(rootSignalUrl.replace("{0}", signingPort), {
      logging: false,
    });

    if (connection.state !== $.signalR.connectionState.disconnected) {
      throw new Error("Already connected");
    }

    return connection;
  };

  const getProxy = async (connection: any) => {
    const proxy = connection.createHubProxy("signingHub");
    await connection.start({ transport: "longPolling" });

    return proxy;
  };

  const version = async () => {
    const connection = await getConnection();

    try {
      const proxy = await getProxy(connection);

      const response = await proxy.invoke("getHubVersion");

      return response?.Version;
    } finally {
      connection?.stop();
    }
  };

  const sign = async (jwt: string) => {
    const connection = await getConnection();
    try {
      const proxy = await getProxy(connection);

      return await proxy.invoke("requestToSign", jwt);
    } finally {
      connection?.stop();
    }
  };

  const testPort = async (port: number) => {
    try {
      const response = await call(rootPortNumberUrl.replace("{0}", `${port}`));

      if (response.status === 200 && response.data?.portData?.portNumber) {
        return response.data.portData.portNumber;
      }
    } catch {
      // Nothing to do
    }

    return null;
  };

  const port = async () => {
    for (let i = 0; i < ports.length; i++) {
      const portResponse = await testPort(ports[i]);
      if (null !== portResponse) {
        return portResponse;
      }
    }

    return null;
  };

  const call = function (url: string, method?: string, config?: AxiosRequestConfig) {
    return axios({
      url,
      method: method || "get",
      withCredentials: true,
      ...(config || {}),
    });
  };

  return {
    port,
    sign,
    version,
  };
};
